import { StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import { weekdayNames, type Session } from "@heraion/shared";

import { formatDayMonth } from "../Logger/formatDate";
import { useLocale } from "../primitives/useLocale";
import type { UiTheme } from "../theme";

// #368: the picked sessions as the days they will be, in the order they were
// tapped: «Day 1 · Pull · Mon 12 Oct». The wide diary's detail pane shows it
// while selecting, and «Create program» shows it again before the save.
export function SelectionOrderList({
  theme,
  sessions,
  title,
  note,
}: {
  theme: UiTheme;
  /** In the order they were picked. */
  sessions: readonly Session[];
  title: string;
  note?: string;
}) {
  const { t } = useTranslation();
  const locale = useLocale();
  const styles = makeStyles(theme);
  const days = weekdayNames(locale, 0);
  return (
    <View style={styles.wrap}>
      <Text style={styles.title} accessibilityRole="header">
        {title}
      </Text>
      {sessions.map((s, i) => {
        const d = new Date(s.startedAt);
        const when = Number.isNaN(d.getTime()) ? "" : `${days[d.getDay()]} ${formatDayMonth(d, locale)}`;
        const day = t("mobile.workouts.dayN", { n: i + 1 });
        const name = s.name ?? t("mobile.workouts.fallbackName");
        // One stop for a screen reader, not three.
        return (
          <View key={s.id} style={styles.row} accessible accessibilityLabel={[day, name, when].filter(Boolean).join(", ")}>
            <Text style={styles.day}>{day}</Text>
            <Text style={styles.name} numberOfLines={2}>
              {name}
            </Text>
            <Text style={styles.when}>{when}</Text>
          </View>
        );
      })}
      {note ? <Text style={styles.note}>{note}</Text> : null}
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, typography: ty } = theme;
  return StyleSheet.create({
    wrap: { gap: s.s2 },
    title: { color: p.ink, fontSize: ty.sizes.sm, fontWeight: "600" },
    row: { flexDirection: "row", alignItems: "baseline", gap: s.s3 },
    day: {
      color: p.ink,
      fontSize: ty.sizes.sm,
      fontWeight: "700",
      fontFamily: ty.fonts.mono,
      fontVariant: ["tabular-nums"],
    },
    name: { flex: 1, color: p.ink, fontSize: ty.sizes.sm },
    when: { color: p.ink3, fontSize: ty.sizes.sm, fontVariant: ["tabular-nums"] },
    note: { color: p.ink2, fontSize: ty.sizes.sm },
  });
}
